/**
 * Extracts the YouTube video ID from various URL formats, including malformed or duplicated URLs.
 * @param {string} url - The YouTube URL or video ID.
 * @returns {string|null} - The clean 11-character video ID or null if not found.
 */
export const getVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const cleanUrl = url.trim();
    if (!cleanUrl) return null;

    // Direct 11-character video ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(cleanUrl)) {
        return cleanUrl;
    }

    // Match standard 11-character video IDs from YouTube URLs (supports nested/repeated prefixes)
    const pattern = /(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/g;
    const matches = [...cleanUrl.matchAll(pattern)];
    if (matches.length > 0) {
        // Take the last match in case of nested/repeated URLs
        const lastMatch = matches[matches.length - 1];
        if (lastMatch && lastMatch[1]) return lastMatch[1];
    }

    // Fallback: extract any 11-char string following watch?v= or v=
    const vParamMatch = cleanUrl.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (vParamMatch && vParamMatch[1]) return vParamMatch[1];

    // Fallback: use URL search params if it's a valid URL
    try {
        const urlObj = new URL(cleanUrl.includes('://') ? cleanUrl : `https://${cleanUrl}`);
        const videoId = urlObj.searchParams.get('v');
        if (videoId) {
            const nestedId = getVideoId(videoId);
            if (nestedId) return nestedId;
            if (videoId.length === 11) return videoId;
        }
    } catch {
        // Not a valid URL, ignore
    }

    return null;
};

/**
 * Returns a canonical clean YouTube watch URL for a video ID or URL.
 * @param {string} urlOrId - A YouTube URL or video ID.
 * @returns {string} - Canonical YouTube URL or original string.
 */
export const getYouTubeUrl = (urlOrId) => {
    if (!urlOrId || typeof urlOrId !== 'string') return '';
    const videoId = getVideoId(urlOrId);
    if (videoId) return `https://www.youtube.com/watch?v=${videoId}`;
    return urlOrId.trim();
};

/**
 * Returns the YouTube thumbnail URL for a given video.
 * @param {string} urlOrId - A YouTube URL or video ID.
 * @param {string} quality - The thumbnail quality (default, hqdefault, maxresdefault, etc.).
 * @returns {string|null} - The thumbnail URL or the original string if not a YouTube URL.
 */
export const getThumbnailUrl = (urlOrId, quality = 'maxresdefault') => {
    const videoId = getVideoId(urlOrId);
    if (!videoId) return urlOrId;
    return `https://i.ytimg.com/vi/${videoId}/${quality}.jpg`;
};

/**
 * Checks if a string is a YouTube URL or valid YouTube video ID.
 * @param {string} url - The URL to check.
 * @returns {boolean} - True if it's a YouTube URL or video ID.
 */
export const isYoutubeUrl = (url) => {
    return !!getVideoId(url);
};
